// /translate/government — Thai government document translation hub (TH)
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute } from "@tanstack/react-router";
import { CertTypeHub } from "@/components/translate/CertTypeHub";
import { getCertType } from "@/data/translation-certtypes";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, serviceWithReviewsSchema, howToSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const cert = getCertType("government")!;

export const Route = createFileRoute("/translate/government")({
  head: () => {
    const url = `${SITE}/translate/government`;
    const title = `${cert.nameTh} | NYC Language Institute`;
    const desc = cert.taglineTh;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/government", "th") }, { name: "description", content: ctaDesc(desc, "/translate/government", "th")},
        { name: "keywords", content: cert.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/translate/government", "th")}, { property: "og:description", content: ctaDesc(desc, "/translate/government", "th")},
        { property: "og:url", content: url },
      ],
      links: canonicalAndHreflang("/translate/government", "/en/translate/government"),
      scripts: [
        speakableSchema(),
        howToSchema({
          name: "ขั้นตอนแปลเอกสารราชการไทย รับรอง NYC",
          description: cert.taglineTh,
          totalTime: "PT2D", estimatedCost: { currency: "THB", value: String(cert.priceFromThb) },
          steps: cert.stepsTh.map((s) => ({ name: s.name, text: s.text })),
        }),
        consolidateGraph([
          serviceWithReviewsSchema({ path: "/translate/government", serviceType: "Thai Government Document Translation", name: title, description: desc, priceFromThb: cert.priceFromThb }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
              mainEntity: cert.faqsTh.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "แปลรายภาษา", item: `${SITE}/translate` },
                { "@type": "ListItem", position: 3, name: "เอกสารราชการ", item: url },
              ],
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({
          url: `${SITE}/api/public/mcp`,
          name: "NYC Language Institute — MCP Server",
          description: "AI agent endpoint exposing Thai government document translation tooling.",
        }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(url)),
      ],
    };
  },
  component: () => <CertTypeHub cert={cert} locale="th" />,
});
